'use client'

import React from 'react'

type Props = {
  // Комментарий на русском: reset приходит из error boundary Next.js (повторная попытка рендера).
  reset?: () => void
}

function sleep(ms: number) {
  return new Promise((r) => setTimeout(r, ms))
}

async function clearPwaCaches() {
  try {
    if (typeof caches === 'undefined') return
    const keys = await caches.keys()
    await Promise.all(keys.filter((k) => k.startsWith('lisad_pwa_')).map((k) => caches.delete(k)))
  } catch {
    // ignore
  }
}

async function refreshServiceWorker() {
  try {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return
    const buildId = process.env.NODE_ENV === 'development' ? 'dev' : String(process.env.NEXT_PUBLIC_APP_BUILD_ID || '').trim()
    const apiBuildId = String(process.env.NEXT_PUBLIC_API_BUILD_ID || '').trim()
    const qs = [buildId ? `v=${encodeURIComponent(buildId)}` : '', apiBuildId ? `api=${encodeURIComponent(apiBuildId)}` : '']
      .filter(Boolean)
      .join('&')
    const swUrl = qs ? `/push-sw.js?${qs}` : '/push-sw.js'
    const reg = await navigator.serviceWorker.register(swUrl, { scope: '/', updateViaCache: 'none' })
    await reg.update().catch(() => {})
  } catch {
    // ignore
  }
}

// Комментарий на русском: “критический” экран — вместо дефолтного "Application error".
export default function CriticalRecoverScreen(props: Props) {
  const [attempt, setAttempt] = React.useState(0)
  const [nextSec, setNextSec] = React.useState(0)
  const [offline, setOffline] = React.useState(false)
  const [done, setDone] = React.useState(false)

  React.useEffect(() => {
    const update = () => setOffline(typeof navigator !== 'undefined' && navigator.onLine === false)
    update()
    window.addEventListener('online', update)
    window.addEventListener('offline', update)
    return () => {
      window.removeEventListener('online', update)
      window.removeEventListener('offline', update)
    }
  }, [])

  React.useEffect(() => {
    let cancelled = false
    const maxAttempts = 6

    const run = async () => {
      // Комментарий на русском: даём браузеру немного времени (iOS иногда “просыпается” не сразу).
      await sleep(250)
      for (let i = 1; i <= maxAttempts && !cancelled; i += 1) {
        setAttempt(i)
        const waitSec = i === 1 ? 2 : Math.min(12, 2 + i * 2)
        for (let s = waitSec; s >= 0 && !cancelled; s -= 1) {
          setNextSec(s)
          await sleep(1000)
        }

        // Комментарий на русском: сначала обновляем SW (подхватить новый деплой),
        // а если не помогло — сбрасываем PWA-кэши и перезагружаем страницу.
        await refreshServiceWorker()
        if (i >= 2) await clearPwaCaches()

        try {
          props.reset?.()
        } catch {
          // ignore
        }
        await sleep(120)
        try {
          window.location.reload()
        } catch {
          // ignore
        }
      }
      if (!cancelled) setDone(true)
    }

    void run()
    return () => {
      cancelled = true
    }
  }, [props.reset])

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--site-bg, #F3F5F7)',
        backdropFilter: 'blur(18px)',
        WebkitBackdropFilter: 'blur(18px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        color: 'var(--site-text, #182433)',
      }}
    >
      <style>{`@keyframes lisadSpin{to{transform:rotate(360deg)}}`}</style>
      <div style={{ width: 'min(520px, 100%)', textAlign: 'center' }}>
        <div
          aria-hidden="true"
          style={{
            width: 34,
            height: 34,
            margin: '0 auto 14px',
            borderRadius: 999,
            border: '3px solid rgba(255,255,255,0.22)',
            borderTopColor: 'rgba(255,255,255,0.92)',
            animation: 'lisadSpin 0.95s linear infinite',
          }}
        />
        <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: 0.2 }}>Загрузка…</div>
        <div style={{ marginTop: 8, fontSize: 13, opacity: 0.85, lineHeight: 1.4 }}>
          {offline ? 'Нет сети — ждём подключение' : done ? 'Не удалось восстановить автоматически' : `Перезапуск через ${nextSec}с (попытка ${attempt}/6)`}
        </div>
        {done && (
          <div style={{ marginTop: 14 }}>
            <button
              type="button"
              onClick={() => {
                void (async () => {
                  await clearPwaCaches()
                  await refreshServiceWorker()
                  try {
                    window.location.reload()
                  } catch {
                    // ignore
                  }
                })()
              }}
              style={{
                padding: '10px 14px',
                borderRadius: 14,
                border: '1px solid rgba(148,163,184,0.35)',
                background: 'var(--site-surface, #fff)',
                color: 'var(--site-text, #182433)',
                fontSize: 13,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Обновить
            </button>
          </div>
        )}
      </div>
    </div>
  )
}


